import type { CostRow } from '../types'
import type { ColumnId } from './columns'

/** The flags `useModuleActions('COST_CONTROL', projectId)` returns. */
export interface ModuleActions { canView: boolean; canCreate: boolean; canEdit: boolean; canDelete: boolean; canApprove: boolean; canExport: boolean; canAdmin: boolean }

export interface BudgetAffordances {
  addNode: boolean
  editName: boolean
  editCode: boolean
  editBudget: boolean
  editForecast: boolean
  move: boolean
  linkBuilding: boolean
  deleteNode: boolean
  importBudget: boolean
  exportBudget: boolean
  lock: boolean
  unlock: boolean
  openSettings: boolean
  changeCurrency: boolean
  changeThreshold: boolean
}

/** Spec §7.1 per action and §1.5 for the lock. The server enforces the same rules; this only hides what it would refuse. */
export function budgetAffordances(a: ModuleActions, locked: boolean): BudgetAffordances {
  return {
    addNode: a.canCreate,
    editName: a.canEdit,
    editCode: a.canEdit,
    editBudget: a.canEdit && !locked,
    editForecast: a.canEdit,
    move: a.canEdit,
    linkBuilding: a.canEdit,
    deleteNode: a.canDelete,
    importBudget: a.canEdit,
    exportBudget: a.canExport,
    lock: a.canApprove && !locked,
    unlock: a.canAdmin && locked,
    openSettings: a.canAdmin || a.canEdit,
    changeCurrency: a.canAdmin,
    changeThreshold: a.canAdmin,
  }
}

export type EditableColumn = 'name' | 'code' | 'originalBudget' | 'estimate' | 'projectedFinalCost'
export interface EditingCell { nodeId: string; column: EditableColumn }

const EDITABLE_ORDER: EditableColumn[] = ['name', 'code', 'originalBudget', 'estimate', 'projectedFinalCost']

type EditableRow = Pick<CostRow, 'kind'> & Partial<Pick<CostRow, 'costBasedForecast'>>

export function isCellEditable(row: EditableRow, column: EditableColumn, af: BudgetAffordances): boolean {
  switch (column) {
    case 'name': return af.editName
    case 'code': return af.editCode && row.kind === 'ITEM'
    case 'originalBudget': return af.editBudget && row.kind === 'ITEM'
    // A contracted item's estimate is not in force; the forecast follows the contract.
    case 'estimate': return af.editForecast && row.kind === 'ITEM' && !row.costBasedForecast
    case 'projectedFinalCost': return af.editForecast && row.kind === 'ITEM'
    default: return false
  }
}

/** Editable cells of a row in screen order; hidden columns are skipped (the name is always shown). */
export const editableColumns = (row: EditableRow, af: BudgetAffordances, visible: ReadonlySet<ColumnId>): EditableColumn[] =>
  EDITABLE_ORDER.filter((c) => (c === 'name' || visible.has(c)) && isCellEditable(row, c, af))
